import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, RefreshCcw, Copy, Check, ExternalLink, Calendar,
  AlertTriangle, Clock, Loader2, Plus, Trash2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCOMMODATIONS = [
  { key: 'appartement-1', label: "L'Appartement Terrasse" },
  { key: 'appartement-2', label: "L'Appartement Panorama" },
  { key: 'gite-seul', label: 'Gite seul (20 pers.)' },
];

const EXPORT_TARGETS = [
  { key: 'airbnb', label: 'Pour Airbnb' },
  { key: 'booking', label: 'Pour Booking.com' },
];

const INTERVAL_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 heure' },
  { value: 120, label: '2 heures' },
  { value: 360, label: '6 heures' },
  { value: 720, label: '12 heures' },
];

function formatSyncDate(isoStr) {
  if (!isoStr) return 'Jamais';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return isoStr; }
}

function detectPlatform(url) {
  const u = (url || '').toLowerCase();
  if (u.includes('airbnb')) return 'Airbnb';
  if (u.includes('booking')) return 'Booking.com';
  if (u.includes('abritel') || u.includes('vrbo') || u.includes('homeaway')) return 'Abritel/Vrbo';
  if (u.includes('gites-de-france')) return 'Gites de France';
  return u.trim() ? 'Autre' : '';
}

export default function AdminIcalSync() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [copied, setCopied] = useState({});
  const token = localStorage.getItem('admin_token');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/ical-config`, { headers });
      setConfig(r.data);
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!token) navigate('/admin');
    else fetchConfig();
  }, [token, navigate, fetchConfig]);

  const updateFeeds = (accKey, feeds) => {
    setConfig(prev => ({
      ...prev,
      accommodations: {
        ...prev.accommodations,
        [accKey]: { ...prev.accommodations?.[accKey], import_urls: feeds },
      },
    }));
  };

  const getFeeds = (accKey) => config?.accommodations?.[accKey]?.import_urls || [];

  const addFeed = (accKey) => {
    updateFeeds(accKey, [...getFeeds(accKey), { id: `new-${Date.now()}`, url: '' }]);
  };

  const removeFeed = (accKey, idx) => {
    updateFeeds(accKey, getFeeds(accKey).filter((_, i) => i !== idx));
  };

  const changeFeedUrl = (accKey, idx, url) => {
    updateFeeds(accKey, getFeeds(accKey).map((f, i) => (i === idx ? { ...f, url } : f)));
  };

  const handleIntervalChange = (val) => {
    setConfig(prev => ({ ...prev, sync_interval_minutes: parseInt(val) }));
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/ical-config`, config, { headers });
      toast.success('Configuration sauvegardee');
      await fetchConfig();
    } catch { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const forceSync = async (accType) => {
    setSyncing(prev => ({ ...prev, [accType]: true }));
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/ical-sync`, { accommodation_type: accType }, { headers });
      const result = r.data[accType];
      if (result?.status === 'success') {
        const detail = (result.feeds || []).map(f => `${f.platform}: ${f.events_imported ?? 0}`).join(', ');
        toast.success(`${result.events_imported} evenement(s) importe(s)${detail ? ` (${detail})` : ''}`);
      } else if (result?.status === 'skipped') {
        toast.info(result.message);
      } else {
        toast.error(result?.message || 'Erreur de synchronisation');
      }
      await fetchConfig();
    } catch { toast.error('Erreur de synchronisation'); }
    setSyncing(prev => ({ ...prev, [accType]: false }));
  };

  const syncAll = async () => {
    setSyncing({ all: true });
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/ical-sync`, {}, { headers });
      let total = 0;
      Object.values(r.data).forEach(v => { if (v.events_imported) total += v.events_imported; });
      toast.success(`Synchronisation terminee : ${total} evenement(s) importe(s)`);
      await fetchConfig();
    } catch { toast.error('Erreur de synchronisation'); }
    setSyncing({});
  };

  const copyUrl = async (key, url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(prev => ({ ...prev, [key]: true }));
      toast.success('Lien copie !');
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    } catch {
      toast.error('Copie impossible — selectionnez le lien manuellement');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#8A9A5B]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="admin-ical-page">
      {/* Header */}
      <header className="bg-[#2C3E30] text-[#F2F0E9] px-6 md:px-12 py-4">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-[#F2F0E9]/70 hover:text-[#F2F0E9]" data-testid="ical-back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-['Playfair_Display'] text-lg md:text-xl">Synchronisation iCal</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ical-sync-all-btn"
              onClick={syncAll}
              disabled={syncing.all}
              variant="ghost"
              className="text-[#C4A962] hover:text-[#C4A962] hover:bg-[#C4A962]/10"
              size="sm"
            >
              {syncing.all ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" strokeWidth={1.5} />}
              Tout synchroniser
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 md:px-12 py-8 space-y-8">
        {/* Info banner */}
        <div className="p-4 rounded-xl bg-[#8A9A5B]/10 border border-[#8A9A5B]/20">
          <p className="text-sm text-[#2C3E30]">
            Synchronisez les disponibilites avec Airbnb <strong>et</strong> Booking.com : ajoutez un flux d&apos;import par plateforme
            pour chaque hebergement. Les reservations externes bloqueront automatiquement les dates sur votre site
            (y compris les interdependances entre hebergements).
          </p>
          <p className="text-xs text-[#595959] mt-2">
            Cote export, donnez a chaque plateforme le lien qui lui est destine (&laquo; Pour Airbnb &raquo; / &laquo; Pour Booking.com &raquo;) :
            chacun inclut les reservations directes du site et celles de l&apos;autre plateforme, sans renvoyer a la plateforme ses propres reservations.
          </p>
        </div>

        {/* Sync interval */}
        <div className="bg-white rounded-xl border border-[#E5E5E0] p-6" data-testid="ical-interval-section">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-semibold text-[#2C3E30]">Frequence de synchronisation</h3>
              <p className="text-xs text-[#595959] mt-1">Les flux iCal seront lus automatiquement selon cet intervalle</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={String(config?.sync_interval_minutes || 60)} onValueChange={handleIntervalChange}>
                <SelectTrigger className="w-[160px] border-[#E5E5E0]" data-testid="ical-interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVAL_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="ical-save-config-btn"
                onClick={saveConfig}
                disabled={saving}
                className="bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040]"
                size="sm"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sauvegarder'}
              </Button>
            </div>
          </div>
        </div>

        {/* Accommodation cards */}
        {ACCOMMODATIONS.map(acc => {
          const accData = config?.accommodations?.[acc.key] || {};
          const feeds = accData.import_urls || [];
          const hasUrl = feeds.some(f => f.url?.trim());
          const lastSync = accData.last_sync;
          const syncStatus = accData.last_sync_status;
          const isError = syncStatus && syncStatus.startsWith('error');

          return (
            <div key={acc.key} className="bg-white rounded-xl border border-[#E5E5E0] overflow-hidden" data-testid={`ical-card-${acc.key}`}>
              {/* Card header */}
              <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#8A9A5B]" strokeWidth={1.5} />
                  <h3 className="font-['Playfair_Display'] text-lg text-[#2C3E30]">{acc.label}</h3>
                </div>
                {lastSync && (
                  <Badge variant="outline" className={isError ? 'border-red-200 text-red-600' : 'border-emerald-200 text-emerald-700'}>
                    {isError ? 'Erreur' : `${accData.events_count || 0} evt(s)`}
                  </Badge>
                )}
              </div>

              <div className="p-6 space-y-5">
                {/* Export section — one URL per platform */}
                <div>
                  <label className="text-xs font-medium text-[#595959] uppercase tracking-wider">Export iCal (Site vers plateformes)</label>
                  <p className="text-xs text-[#888] mt-1 mb-2">Collez chaque lien dans la plateforme correspondante (&laquo; Importer un calendrier &raquo;).</p>
                  <div className="space-y-2">
                    {EXPORT_TARGETS.map(t => {
                      const exportUrl = `${BACKEND_URL}/api/ical/${acc.key}.ics?exclude=${t.key}`;
                      const copyKey = `${acc.key}-${t.key}`;
                      return (
                        <div key={t.key} className="flex items-center gap-2">
                          <span className="w-[130px] shrink-0 text-xs font-medium text-[#2C3E30]">{t.label}</span>
                          <Input
                            readOnly
                            value={exportUrl}
                            className="bg-[#F5F5F0] text-xs font-mono text-[#595959] border-[#E5E5E0]"
                            data-testid={`ical-export-url-${acc.key}-${t.key}`}
                          />
                          <Button
                            data-testid={`ical-copy-export-${acc.key}-${t.key}`}
                            onClick={() => copyUrl(copyKey, exportUrl)}
                            variant="outline"
                            size="sm"
                            className="shrink-0 border-[#E5E5E0]"
                          >
                            {copied[copyKey] ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" strokeWidth={1.5} />}
                          </Button>
                          <a href={exportUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                            <Button variant="outline" size="sm" className="border-[#E5E5E0]" data-testid={`ical-open-export-${acc.key}-${t.key}`}>
                              <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Import section — multiple feeds */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#595959] uppercase tracking-wider">Import iCal (Plateformes vers Site)</label>
                    <Button
                      data-testid={`ical-add-feed-${acc.key}`}
                      onClick={() => addFeed(acc.key)}
                      variant="ghost"
                      size="sm"
                      className="text-[#8A9A5B] hover:text-[#8A9A5B] hover:bg-[#8A9A5B]/10 h-7 text-xs"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2} /> Ajouter un flux
                    </Button>
                  </div>
                  <p className="text-xs text-[#888] mt-1 mb-2">Un flux par plateforme (Airbnb, Booking...). La plateforme est detectee automatiquement.</p>
                  <div className="space-y-2">
                    {feeds.length === 0 && (
                      <p className="text-xs text-[#aaa] italic py-1" data-testid={`ical-no-feeds-${acc.key}`}>Aucun flux d&apos;import configure.</p>
                    )}
                    {feeds.map((feed, idx) => {
                      const feedError = feed.last_sync_status && feed.last_sync_status.startsWith('error');
                      const platform = feed.platform || detectPlatform(feed.url);
                      return (
                        <div key={feed.id || idx} className="space-y-1">
                          <div className="flex items-center gap-2">
                            {platform && (
                              <Badge variant="outline" className="shrink-0 w-[110px] justify-center text-[11px] border-[#E5E5E0] text-[#2C3E30]">
                                {platform}
                              </Badge>
                            )}
                            <Input
                              value={feed.url || ''}
                              onChange={(e) => changeFeedUrl(acc.key, idx, e.target.value)}
                              placeholder="https://www.airbnb.fr/calendar/ical/... ou https://ical.booking.com/..."
                              className="text-sm border-[#E5E5E0]"
                              data-testid={`ical-import-url-${acc.key}-${idx}`}
                            />
                            <Button
                              data-testid={`ical-remove-feed-${acc.key}-${idx}`}
                              onClick={() => removeFeed(acc.key, idx)}
                              variant="ghost"
                              size="sm"
                              className="shrink-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </div>
                          {feed.last_sync && (
                            <p className={`text-[11px] pl-1 ${feedError ? 'text-red-500' : 'text-[#8A9A5B]'}`} data-testid={`ical-feed-status-${acc.key}-${idx}`}>
                              {feedError
                                ? `Erreur — ${feed.last_sync_status}`
                                : `${feed.events_count || 0} evt(s) — sync ${formatSyncDate(feed.last_sync)}`}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button
                      data-testid={`ical-sync-${acc.key}`}
                      onClick={() => forceSync(acc.key)}
                      disabled={!hasUrl || syncing[acc.key]}
                      variant="outline"
                      size="sm"
                      className="border-[#E5E5E0]"
                    >
                      {syncing[acc.key] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" strokeWidth={1.5} />}
                      Synchroniser
                    </Button>
                  </div>
                </div>

                {/* Sync status */}
                {lastSync && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${isError ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`} data-testid={`ical-status-${acc.key}`}>
                    {isError ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                    <span>
                      Derniere sync : {formatSyncDate(lastSync)}
                      {!isError && ` — ${accData.events_count || 0} evenement(s) importe(s) au total`}
                      {isError && ` — ${syncStatus}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Save button */}
        <div className="flex justify-end">
          <Button
            data-testid="ical-save-all-btn"
            onClick={saveConfig}
            disabled={saving}
            className="bg-[#2C3E30] text-[#F2F0E9] hover:bg-[#3a5040] rounded-full px-8"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Sauvegarder la configuration
          </Button>
        </div>
      </main>
    </div>
  );
}
